import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { DEFAULT_THEME_KEY, THEME_PRESETS, ThemeKey } from './themes';

interface ThemeContextValue {
  themeKey: ThemeKey;
  setThemeKey: (key: ThemeKey) => void;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
}

const LOCAL_STORAGE_KEY = 'app_theme_key';
const LOCAL_STORAGE_DARK_KEY = 'app_theme_dark';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useAppTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return ctx;
};

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [themeKey, setThemeKey] = useState<ThemeKey>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY) as ThemeKey | null;
    return saved && THEME_PRESETS[saved] ? saved : DEFAULT_THEME_KEY;
  });
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_DARK_KEY);
    return saved ? saved === 'true' : false;
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, themeKey);
  }, [themeKey]);
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_DARK_KEY, String(darkMode));
  }, [darkMode]);

  const baseTheme = useMemo(() => THEME_PRESETS[themeKey].theme, [themeKey]);

  const value = useMemo(
    () => ({ themeKey, setThemeKey, darkMode, setDarkMode }),
    [themeKey, darkMode]
  );

  // 将主题主色、链接色、背景色、文本色注入到全局 CSS 变量中，供非 antd 元素使用
  useEffect(() => {
    const { token } = baseTheme;
    const root = document.documentElement;
    const primary = token?.colorPrimary || '#000000';
    const link = token?.colorLink || primary;
    const bg = darkMode ? '#141414' : '#ffffff';
    const text = darkMode ? '#f0f0f0' : '#000000';
    root.style.setProperty('--app-primary-color', primary);
    root.style.setProperty('--app-link-color', link);
    root.style.setProperty('--app-bg-color', bg);
    root.style.setProperty('--app-text-color', text);
  }, [baseTheme, darkMode]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          ...baseTheme,
          algorithm: darkMode
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
